import { useState, useEffect } from 'react';
import { hysteriFetch, wrappers } from 'hysterics';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';

const gmPaymentsFetcher = hysteriFetch('ws-detailed-pricing-gm-payments');

/**
 * Transform finance data according to GM Display Rules
 * Finance Rule: Display lowest monthly payment for longest finance term where payment > $0
 * @param {Object} data - Raw VCDA features response
 * @returns {Object} Transformed finance data
 */
const transformFinanceData = (data) => {
	const optIn = data.gmFinanceOptIn || false;
	const financeMathBoxes = data.gmFinanceMathBoxes || [];

	// Not opted in at all
	if (!optIn) {
		return {
			optIn: false,
			payment: null,
			term: null,
			marketingLabel: null,
			showContactDealer: false
		};
	}

	// Opted in but no math boxes - show Contact Dealer
	if (financeMathBoxes.length === 0) {
		return {
			optIn: true,
			payment: null,
			term: null,
			marketingLabel: null,
			showContactDealer: true
		};
	}

	// Calculate estimated monthly payment for each term
	const termsWithPayments = financeMathBoxes
		.map((term) => {
			// Get net price with dealer fees from termMathBox
			const netPriceItem = term.termMathBox?.find(
				(item) => item.type === 'net_price_with_dealer_fees'
			);
			const netPrice = netPriceItem?.amount || 0;

			// Calculate monthly payment
			const estimatedPayment =
				(netPrice / 1000) * term.paymentPerThousand;

			return {
				...term,
				netPrice,
				estimatedPayment
			};
		})
		.filter((term) => term.estimatedPayment > 0); // Only valid payments

	// Check if all terms have invalid payments (Contact Dealer scenario)
	if (termsWithPayments.length === 0) {
		return {
			optIn,
			payment: null,
			term: null,
			marketingLabel: null,
			showContactDealer: true
		};
	}

	// Sort by lowest payment first, then by longest term
	termsWithPayments.sort((a, b) => {
		if (a.estimatedPayment === b.estimatedPayment) {
			return b.term - a.term; // Longest term wins on tie
		}
		return a.estimatedPayment - b.estimatedPayment; // Lowest payment wins
	});

	const selectedTerm = termsWithPayments[0];

	return {
		optIn,
		payment: Math.round(selectedTerm.estimatedPayment),
		term: selectedTerm.term,
		marketingLabel: selectedTerm.marketingLabel || null,
		showContactDealer: false
	};
};

/**
 * Transform lease data according to GM Display Rules
 * Lease Rule: Display lowest monthly payment where contactDealer flags are false
 * @param {Object} data - Raw VCDA features response
 * @returns {Object} Transformed lease data
 */
const transformLeaseData = (data) => {
	const optIn = data.gmLeaseOptIn || false;
	const leaseMathBoxes = data.gmLeaseMathBoxes || [];

	// Not opted in at all
	if (!optIn) {
		return {
			optIn: false,
			payment: null,
			term: null,
			dueAtSigning: null,
			marketingLabel: null,
			showContactDealer: false,
			showDisclosure: false
		};
	}

	// Opted in but no math boxes - show Contact Dealer
	if (leaseMathBoxes.length === 0) {
		return {
			optIn: true,
			payment: null,
			term: null,
			dueAtSigning: null,
			marketingLabel: null,
			showContactDealer: true,
			showDisclosure: false
		};
	}

	// Filter terms where contact dealer flags are false
	// NOTE: Sample data doesn't include contactDealerWithOutFees/contactDealerWithFees
	// For now, we'll use a placeholder calculation
	// TODO: Update when actual field names are confirmed (see TA3712468)
	const validTerms = leaseMathBoxes.filter(() => {
		// Placeholder: assume all terms are valid for now
		// In production, check: term.contactDealerWithOutFees === false
		return true;
	});

	// Check if all terms require "Contact Dealer"
	if (validTerms.length === 0) {
		return {
			optIn,
			payment: null,
			term: null,
			dueAtSigning: null,
			marketingLabel: null,
			showContactDealer: true,
			showDisclosure: false
		};
	}

	// Calculate estimated monthly payment for each valid term
	// NOTE: Lease monthly payment calculation method is unclear from sample data
	// Using a placeholder calculation based on residual and interest
	// TODO: Confirm calculation method with VCDA team (see TA3712468)
	const termsWithPayments = validTerms.map((term) => {
		const netPriceItem = term.termMathBox?.find(
			(item) => item.type === 'net_price'
		);
		const netPrice = netPriceItem?.amount || 0;

		// Placeholder calculation (simplified lease payment estimate)
		// In production, use actual lease payment field or confirmed formula
		const residualValue = netPrice * (term.residualRate / 100);
		const depreciation = netPrice - residualValue;
		const estimatedPayment = depreciation / term.term;

		return {
			...term,
			netPrice,
			estimatedPayment
		};
	});

	// Sort by lowest payment
	termsWithPayments.sort((a, b) => a.estimatedPayment - b.estimatedPayment);

	const selectedTerm = termsWithPayments[0];

	// Get marketing label from disclosure or other field
	// NOTE: Sample data shows lease doesn't have marketingLabel at top level
	// Using disclosure.value as fallback
	const marketingLabel = selectedTerm.marketingLabel || null;

	// Calculate due at signing (placeholder)
	// TODO: Confirm where this value comes from in actual response
	const dueAtSigning = null;

	return {
		optIn,
		payment: Math.round(selectedTerm.estimatedPayment),
		term: selectedTerm.term,
		dueAtSigning,
		marketingLabel,
		showContactDealer: false,
		showDisclosure: true
	};
};

/**
 * Transform VCDA Features response according to GM Display Rules
 * @param {Object} data - Raw VCDA features response
 * @returns {Object} Transformed payment data
 */
const transformGMPaymentData = (data) => {
	if (!data) return null;

	const financeData = transformFinanceData(data);
	const leaseData = transformLeaseData(data);

	return {
		finance: financeData,
		lease: leaseData,
		rawData: data // Keep for reference during development
	};
};

// Export transformation functions for testing
export { transformFinanceData, transformLeaseData, transformGMPaymentData };

/**
 * Custom hook to fetch and transform GM payment data
 * @param {Object} params
 * @param {string} params.vehicleId - Vehicle UUID
 * @param {string} params.accountId - Account ID (optional)
 * @param {string} params.locale - Locale for payment data (e.g., 'en_US', 'es_MX', 'fr_CA')
 * @param {boolean} params.enabled - Whether to fetch (for conditional fetching, defaults to false)
 * @param {string} params.eligibilityReason - Debug reason for eligibility (optional)
 * @returns {Object} { gmPayments, isLoading, error }
 */
export const useGMPayments = ({
	vehicleId,
	accountId,
	locale = 'en_US',
	enabled = false,
	eligibilityReason = ''
}) => {
	const [gmPayments, setGmPayments] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	// Use default timeout and retries
	const clientTimeout = 5000;
	const clientRetries = 1;

	useEffect(() => {
		// Debug logging for eligibility (only logs with ?_mode=debug in browser)
		if (isBrowser && eligibilityReason) {
			let urlParams = {};
			if (typeof window.DDC?.getUrlParams === 'function') {
				urlParams = window.DDC.getUrlParams();
			}

			// Only log to console if _mode=debug is present in URL
			if (urlParams._mode === 'debug') {
				/* eslint-disable no-console */
				if (!enabled) {
					console.log(`GM Payments Not Eligible: ${eligibilityReason}`);
				} else {
					console.log(`GM Payments Eligible: ${eligibilityReason}`);
				}
				/* eslint-enable no-console */
			}
		}

		// Don't fetch if disabled or no vehicleId
		if (!enabled || !vehicleId) {
			return;
		}

		const fetchGMPaymentData = async () => {
			setIsLoading(true);
			setError(null);

			try {
				// Use widget proxy path (not /api/gm-payments)
				const requestUrl =
					'/api/widget/ws-detailed-pricing/gm-payments';
				const requestBody = {
					vehicleId,
					accountId
				};

				// POST request with timeout and retry wrappers
				const response = await gmPaymentsFetcher(
					requestUrl,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json;charset=UTF-8'
						},
						body: JSON.stringify(requestBody)
					},
					{
						[wrappers.timeout.contextKey]: {
							timeout: clientTimeout
						},
						[wrappers.retry.contextKey]: {
							retries: clientRetries
						}
					}
				);

				if (!response) {
					// No response - set empty state
					setGmPayments(null);
					return;
				}

				if (response.errors) {
					throw new Error(response.errors.message || 'Unknown error');
				}

				// Extract locale-specific payment data from gmTier1PaymentInfo
				const gmTier1PaymentInfo = response.data?.gmTier1PaymentInfo || {};
				let paymentData = gmTier1PaymentInfo[locale];

				// Fallback to en_US if requested locale is not available
				if (!paymentData && locale !== 'en_US') {
					logger.warn(
						`GM_PAYMENTS/CLIENT/LOCALE_FALLBACK: Locale '${locale}' not found, falling back to 'en_US'`
					);
					paymentData = gmTier1PaymentInfo.en_US;
				}

				// If still no data, log and return null
				if (!paymentData) {
					logger.warn(
						`GM_PAYMENTS/CLIENT/NO_LOCALE_DATA: No payment data found for locale '${locale}' or fallback 'en_US'`
					);
					setGmPayments(null);
					return;
				}

				// Transform the data according to GM Display Rules
				const transformedData = transformGMPaymentData(paymentData);
				setGmPayments(transformedData);
			} catch (err) {
				const errorMessage = `GM_PAYMENTS/CLIENT/FETCH_ERROR: ${err.message}`;
				logger.error(errorMessage);
				setError(errorMessage);
				setGmPayments(null);
			} finally {
				setIsLoading(false);
			}
		};

		fetchGMPaymentData();
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [vehicleId, accountId, locale, enabled, eligibilityReason]);

	return { gmPayments, isLoading, error };
};
